import { fetchApiWithRedirects, isRedirectFollowedError } from '~/utils/apiFetch'
import { classifyNetworkError } from '~/utils/networkError'
import { useUserStore } from '~/store/userStore.js'
import { useLastVisitedProducts } from '~/store/lastVisitedProducts.js'

const categoryBaseSlug = (slugParam: string | string[] | undefined) => {
    const slug = Array.isArray(slugParam) ? slugParam.join('/') : String(slugParam || '')
    return slug
        .split('/')
        .filter(Boolean)
        .filter((part) => !part.includes('='))
        .join('/')
}

export default defineNuxtRouteMiddleware(async (to, from) => {
    if (import.meta.server) return

    const nuxtApp = useNuxtApp()
    if (nuxtApp.payload.error) return

    const slugParam = to.params.slug ?? []
    const slug = Array.isArray(slugParam) ? slugParam.join('/') : slugParam
    const baseSlug = categoryBaseSlug(slugParam) || slug
    const queryString = new URLSearchParams(to.query as Record<string, string>).toString()
    // Same key as pages/[...slug].vue (filters stripped) so KeepAlive remounts find payload.
    const key = `page-${baseSlug}${queryString ? `-${queryString}` : ''}`

    // Filter toggles use router.replace on the same category — skip /page refetch.
    if (from?.params?.slug != null) {
        const toBase = categoryBaseSlug(to.params.slug as string | string[])
        const fromBase = categoryBaseSlug(from.params.slug as string | string[])
        if (toBase && toBase === fromBase) {
            return
        }
    }

    if (nuxtApp.isHydrating && nuxtApp.payload.data[key] !== undefined) return

    const config = useRuntimeConfig()
    const userStore = useUserStore()
    const lastVisitedProducts = useLastVisitedProducts()

    const {authHeaders} = await import('~/composables/apiAuth.js')
    const headers = authHeaders({
        accept: 'application/json',
        'Accept-Language': config.public.locale,
        referer: config.public.baseURL,
        lastVisitedProducts: JSON.stringify(lastVisitedProducts.products),
    })

    try {
        nuxtApp.static.data[key] = await fetchApiWithRedirects(`/page/${slug}`, {
            query: {...to.query},
            timeout: 60000,
            headers,
        }, 0, config.public) as Record<string, any>
    } catch (err: any) {
        if (isRedirectFollowedError(err)) {
            return abortNavigation()
        }

        const classification = classifyNetworkError(err)
        const statusCode = classification.statusCode

        if (classification.isAborted) {
            return abortNavigation()
        }

        if (classification.isOffline) {
            return abortNavigation(createError({ statusCode: 503, statusMessage: 'Temporary Network Error', message: err.message }))
        }

        if (statusCode === 410) {
            return abortNavigation(createError({ statusCode: 410, statusMessage: 'Page Is Gone' }))
        }

        if (statusCode === 405) {
            return abortNavigation(createError({ statusCode: 404, statusMessage: 'Page Not Found' }))
        }

        if (statusCode === 401) {
            return navigateTo(`/login?redirect=${to.fullPath}`)
        }

        if (statusCode === 404 || statusCode === 403) {
            return abortNavigation(createError({ statusCode: 404, statusMessage: 'Page Not Found', message: err.message }))
        }

        if (classification.isNetworkNoResponse) {
            return abortNavigation(createError({ statusCode: 503, statusMessage: 'Temporary Network Error', message: err.message }))
        }

        return abortNavigation(createError({ statusCode: 500, statusMessage: 'Internal Error', message: err.message }))
    }
})
